import { type MouseEvent as ReactMouseEvent } from 'react';
import { useIntl } from 'react-intl';

import {
  CrosshairIcon,
  PanHorizontalIcon,
  ZoomToSelectionIcon,
} from '@dynatrace/strato-icons';

import { DownloadDataButton } from './DownloadDataButton.js';
import { ExploreButton } from './ExploreButton.js';
import { PanButton } from './PanButton.js';
import { ResetButton } from './ResetButton.js';
import { ZoomInOutButtons } from './ZoomInOutButtons.js';
import { ZoomModeButton } from './ZoomModeButton.js';
import { InteractionMode } from '../../../core/components/interactions-overlay/types.js';
import {
  ChartToolbarRenderer,
  type MenuDisplayMode,
} from '../../../core/components/toolbar/ChartToolbarRenderer.js';
import { ChartOverlaysElevation } from '../../../core/constants.js';
import { getA11yToolbarConfig } from '../../../core/utils/zoom.js';
import { defaultToolbarPlacement } from '../../constants/defaults.js';
import {
  useInteractionModeState,
  useToolbarState,
} from '../../hooks/store/selectors.js';
import { useHistogramFilteredData } from '../../hooks/useHistogramFilteredData.js';
import { useHistogramInteractions } from '../../hooks/useHistogramInteractions.js';
import { useHistogramSelectionAreaRef } from '../../hooks/useHistogramSelectionAreaRef.js';
import { useHistogramToolbar } from '../../hooks/useHistogramToolbar.js';
import { HISTOGRAM_MESSAGES } from '../../messages/tooltip-text.js';
import { buildAndDownloadCsv } from '../../utils/build-and-download-csv.js';

type HistogramToolbarProps = {
  onMouseEnter?: (event?: ReactMouseEvent<HTMLDivElement, MouseEvent>) => void;
  onMouseLeave?: (event?: ReactMouseEvent<HTMLDivElement, MouseEvent>) => void;
};

const getMenuIcon = (
  interactionMode: InteractionMode,
  isZoomEnabled: boolean | undefined,
) => {
  switch (interactionMode) {
    case 'pan':
      return <PanHorizontalIcon />;
    case 'zoom':
      return isZoomEnabled ? <ZoomToSelectionIcon /> : <CrosshairIcon />;
    default:
      return <CrosshairIcon />;
  }
};

export const HistogramToolbar = ({
  onMouseEnter,
  onMouseLeave,
}: HistogramToolbarProps) => {
  const { placement, collapsed, draggable, downloadData, intents } =
    useHistogramToolbar();
  const { visible } = useToolbarState();
  const interactionMode = useInteractionModeState();
  const { zoom, zoomX, pan } = useHistogramInteractions();
  const intl = useIntl();
  const selectionAreaRef = useHistogramSelectionAreaRef();

  const isZoomEnabled = zoom?.enabled;
  const isPanEnabled = pan?.enabled;
  const isDownloadEnabled = downloadData?.enabled ?? false;

  const anyInteractionEnabled =
    isZoomEnabled ||
    zoomX?.enabled ||
    isPanEnabled ||
    isDownloadEnabled ||
    intents;

  const menuIcon = getMenuIcon(interactionMode, isZoomEnabled);

  const menuLabel =
    !isPanEnabled && !isZoomEnabled
      ? intl.formatMessage(HISTOGRAM_MESSAGES.zoomModeExplore)
      : undefined;

  let menuDisplayMode: MenuDisplayMode;

  if (isPanEnabled || isZoomEnabled) {
    if (isZoomEnabled || isDownloadEnabled) {
      menuDisplayMode = 'always';
    } else {
      menuDisplayMode = 'expanded';
    }
  } else {
    if (isDownloadEnabled) {
      menuDisplayMode = 'collapsed';
    } else {
      menuDisplayMode = 'never';
    }
  }

  const onClose = () =>
    selectionAreaRef ? selectionAreaRef.current?.focus() : undefined;

  const { series } = useHistogramFilteredData();

  /** Handles the download button when clicked. */
  const handleDownloadDataToolbar = () => buildAndDownloadCsv(series);

  const showDefaultToolbarDownloadButton = isDownloadEnabled && !intents;

  return anyInteractionEnabled ? (
    <ChartToolbarRenderer
      visible={visible}
      placement={placement || defaultToolbarPlacement}
      collapsed={collapsed}
      draggable={draggable}
      initialA11yState={getA11yToolbarConfig({
        zoomX,
        pan,
        zoom,
        download: downloadData,
        intent: !!intents,
      })}
      zIndex={ChartOverlaysElevation.toolbar}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      menuIcon={menuIcon}
      menuTriggerLabel={menuLabel}
      menuDisplayMode={menuDisplayMode}
      onClose={onClose}
      isDownloadEnabled={isDownloadEnabled}
      intents={intents}
      handleDownloadDataToolbar={handleDownloadDataToolbar}
      DownloadButton={() => <DownloadDataButton />}
    >
      <>
        <ExploreButton />
        {isZoomEnabled && <ZoomModeButton />}
        {isPanEnabled && <PanButton />}
      </>
      {isZoomEnabled && <ZoomInOutButtons />}
      <ResetButton />
      {showDefaultToolbarDownloadButton && <DownloadDataButton />}
    </ChartToolbarRenderer>
  ) : null;
};
